import React, { memo, useCallback } from 'react';
import { convertToLocaleCurrency } from '../../../utils/Currency.Util';
import { formatDate, formatDateTime } from '../../../utils/Date.Util';
import { Label, LabelType } from '../Label.component';
import { ColorType } from '../index';
import { InputProps } from './Grid.component';
import { GridColumnType } from './enums/Grid-Column-Type.enum';
import { GridColumn } from './types/Grid-Column.interface';

export const GridBody = memo(
  ({
    columns,
    data,
    primaryKey,
    onRowClick,
    pageIndex,
    local,
    rowCount = 20,
    hasBottomBorder = true,
    skeletonRowCount = 8,
    isLoading = false,
    hasRowBottomBorder = false,
    verticalAlign = 'align-inherit',
    dataTestId = '',
    rowClassName,
  }: InputProps) => {
    const formatData = (col: GridColumn, dataRow: any) => {
      let currentData = '';

      if (col.key.indexOf('.') !== -1) {
        currentData = dataRow;
        col.key.split('.').forEach((key: any) => {
          currentData = currentData[key];
        });
      } else if (col.key.indexOf('|') !== -1) {
        const keys = col.key.split('|');
        currentData = dataRow[keys[0]];

        if (!currentData) {
          currentData = dataRow[keys[1]];
        }
      } else {
        currentData = dataRow[col.key];
      }

      if (col.type === GridColumnType.DATE) {
        if (currentData) {
          return formatDate(new Date(currentData), col.format);
        }
      } else if (col.type === GridColumnType.DATETIME) {
        if (currentData) {
          return formatDateTime(new Date(currentData.split('[')[0]), col.format);
        }
      } else if (col.type === GridColumnType.CURRENCY) {
        if (currentData) {
          return convertToLocaleCurrency(+currentData);
        }
      }

      return currentData;
    };

    const rowClicked = useCallback(
      (row: any) => {
        onRowClick && onRowClick(row);
      },
      [onRowClick],
    );

    const renderRows = (col: any, dataRow: any) => {
      if (col.component) {
        const Component = col.component;
        return <Component {...dataRow} />;
      }
      return (
        <Label
          key={dataRow.toString()}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          text={isLoading ? '' : formatData(col, dataRow)}
          isLoading={isLoading}
          skeletonHeight='h-4'
          skeletonWidth='w-14'
        />
      );
    };
    const handleCellClick = (event: React.MouseEvent, col: GridColumn) => {
      if (col.onCellClick) {
        event.stopPropagation();
      }
    };

    const rowKeydown = (e: React.KeyboardEvent, dataRow: any) => {
      if (e.key === 'Enter' || e.key === ' ') {
        rowClicked(dataRow);
      }
    };

    if (isLoading) {
      return (
        <tbody className={hasBottomBorder ? 'table-item border-b border-grey2' : 'table-item'}>
          {Array(skeletonRowCount)
            .fill(0)
            .map((dataRow: any, index: number) => (
              <tr
                // These are just skeleton rows, so we allow index as keys
                // eslint-disable-next-line react/no-array-index-key
                key={`skeleton-row-${index}`}
                className={`row ${dataRow.bg ? dataRow.bg : 'bg-white'}`}
              >
                {columns.map((col: GridColumn) => (
                  <td
                    className={`py-2.5 px-3 ${verticalAlign}`}
                    key={col.key}
                    onClick={(event: React.MouseEvent<HTMLTableCellElement>) => {
                      handleCellClick(event, col);
                    }}
                  >
                    {renderRows(col, dataRow)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      );
    }
    return (
      <tbody className={hasBottomBorder ? 'table-item border-b border-grey2' : 'table-item'}>
        {(local
          ? (data || []).filter(
              (d: any, index) =>
                index >= ((pageIndex || 1) - 1) * rowCount &&
                index < ((pageIndex || 1) - 1) * rowCount + rowCount,
            )
          : data || []
        ).map((dataRow: any, index: number) => (
          <tr
            tabIndex={0}
            onKeyDown={(e) => rowKeydown(e, dataRow)}
            onClick={() => rowClicked(dataRow)}
            key={dataRow[primaryKey] ?? `row-${index}`}
            className={`row-item row ${dataRow.bg ? dataRow.bg : 'bg-white'} ${
              hasRowBottomBorder && 'border-b border-grey2'
            } ${onRowClick ? 'hover:bg-grey1 cursor-pointer' : ''} ${
              rowClassName ? rowClassName(dataRow) : ''
            }`}
            data-testid={dataTestId}
          >
            {columns.map((col: GridColumn) => (
              <td
                className={`py-2.5 px-3 ${verticalAlign}`}
                key={col.key}
                onClick={(event: React.MouseEvent<HTMLTableCellElement>) => {
                  handleCellClick(event, col);
                }}
              >
                {renderRows(col, dataRow)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  },
);

// Added display name to fix eslint error Component definition is missing display name

GridBody.displayName = 'GridBody';
